/**
 * Episode Redirect Page
 * Route: /e/:code
 *
 * Smart redirect page for episode deep links:
 * 1. Resolves short code to cue_id via API
 * 2. Attempts to open app via cuesesh://episode/{cueId}
 * 3. Falls back to App Store after timeout
 *
 * This page is seen briefly (<2 seconds) before redirect.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// App Store URL for CueSesh
const APP_STORE_URL = 'https://apps.apple.com/app/cuesesh/id6754615691';

// Timeout before falling back to App Store (milliseconds)
const APP_OPEN_TIMEOUT = 1500;

// API base URL
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function EpisodeRedirect() {
  const { code } = useParams();
  const [status, setStatus] = useState('resolving'); // 'resolving' | 'opening' | 'redirecting' | 'error'
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setError('No share code provided');
      return;
    }

    let timeoutId = null;
    let hasRedirected = false;

    const handleRedirect = async () => {
      try {
        // Step 1: Resolve short code to cue_id
        setStatus('resolving');

        const response = await fetch(`${API_BASE}/api/share/${code}`);

        if (!response.ok) {
          if (response.status === 404) {
            setStatus('error');
            setError('This link has expired or is invalid');
            return;
          }
          throw new Error('Failed to resolve share code');
        }

        const data = await response.json();

        if (!data.success || !data.cueId) {
          setStatus('error');
          setError('Invalid share link');
          return;
        }

        const cueId = data.cueId;

        // Step 2: Attempt to open the app
        setStatus('opening');

        // Construct deep link URL
        const deepLinkUrl = `cuesesh://episode/${encodeURIComponent(cueId)}`;

        // Set up fallback to App Store
        timeoutId = setTimeout(() => {
          if (!hasRedirected) {
            hasRedirected = true;
            setStatus('redirecting');
            window.location.href = APP_STORE_URL;
          }
        }, APP_OPEN_TIMEOUT);

        // Try to open the app
        // This will work if app is installed, fail silently if not
        window.location.href = deepLinkUrl;

      } catch (err) {
        console.error('[EpisodeRedirect] Error:', err);
        setStatus('error');
        setError('Something went wrong. Please try again.');
      }
    };

    handleRedirect();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [code]);

  // Render minimal UI (user sees this briefly)
  return (
    <>
      <Helmet>
        {/* OG tags for link previews - uses generic CueSesh branding */}
        <title>Listen on CueSesh</title>
        <meta property="og:title" content="Listen on CueSesh" />
        <meta property="og:description" content="The podcast app built around your time" />
        <meta property="og:image" content="https://cuesesh.com/og-image.png" />
        <meta property="og:url" content={`https://cuesesh.com/e/${code}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Listen on CueSesh" />
        <meta name="twitter:description" content="The podcast app built around your time" />
        <meta name="twitter:image" content="https://cuesesh.com/twitter-card.png" />
      </Helmet>

      <div style={styles.container}>
        <div style={styles.content}>
          {/* Logo */}
          <img
            src="/cuesesh-logo.svg"
            alt="CueSesh"
            style={styles.logo}
          />

          {/* Status message */}
          {status === 'resolving' && (
            <p style={styles.message}>Loading...</p>
          )}

          {status === 'opening' && (
            <p style={styles.message}>Opening CueSesh...</p>
          )}

          {status === 'redirecting' && (
            <p style={styles.message}>Redirecting to App Store...</p>
          )}

          {status === 'error' && (
            <>
              <p style={styles.errorMessage}>{error}</p>
              <a href={APP_STORE_URL} style={styles.button}>
                Get CueSesh
              </a>
            </>
          )}

          {/* Loading spinner for non-error states */}
          {status !== 'error' && (
            <div style={styles.spinner} />
          )}
        </div>
      </div>
    </>
  );
}

// Minimal inline styles (page is seen briefly)
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  content: {
    textAlign: 'center',
    color: 'white',
  },
  logo: {
    width: '80px',
    height: '80px',
    marginBottom: '24px',
  },
  message: {
    fontSize: '18px',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '16px',
  },
  errorMessage: {
    fontSize: '16px',
    color: 'rgba(255, 100, 100, 0.9)',
    marginBottom: '24px',
  },
  button: {
    display: 'inline-block',
    padding: '12px 32px',
    backgroundColor: '#80578C',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
  spinner: {
    width: '32px',
    height: '32px',
    margin: '0 auto',
    border: '3px solid rgba(255, 255, 255, 0.2)',
    borderTopColor: '#80578C',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframes for spinner (injected once)
if (typeof document !== 'undefined' && !document.getElementById('episode-redirect-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'episode-redirect-styles';
  styleSheet.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}
